import { useMemo, useState } from 'react';
import { titles } from '@/mocks/titles';
import { filterTitles, paginate, defaultFilters, type FilterState } from '@/utils/titles';
import Breadcrumb from '@/components/base/Breadcrumb';
import FilterPanel from '@/components/feature/FilterPanel';
import TitleRow from '@/components/feature/TitleRow';
import Pagination from '@/components/base/Pagination';
import { usePageMeta } from '@/hooks/usePageMeta';

interface CategoryPageProps {
  type: 'movie' | 'tv';
}

const PAGE_SIZE = 6;

const config = {
  movie: {
    title: '电影分类',
    heading: '电影分类',
    desc: '海量电影资源，涵盖多类型多地区最新电影',
    crumb: '电影',
  },
  tv: {
    title: '电视剧分类',
    heading: '电视剧分类',
    desc: '最新电视剧全集追踪，热门剧集持续更新',
    crumb: '电视剧',
  },
};

export default function CategoryPage({ type }: CategoryPageProps) {
  const [filters, setFilters] = useState<FilterState>({ ...defaultFilters });
  const [page, setPage] = useState(1);

  const baseList = useMemo(() => titles.filter((item) => item.type === type), [type]);

  const filtered = useMemo(() => filterTitles(baseList, filters), [baseList, filters]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = useMemo(() => paginate(filtered, safePage, PAGE_SIZE), [filtered, safePage]);

  const meta = config[type];

  usePageMeta({
    title: `${meta.title} - 秋霞电影网`,
    description: `${meta.desc}，支持按类型、地区、年份${type === 'tv' ? '、更新状态' : ''}等多维筛选，快速找到想看的${type === 'movie' ? '电影' : '电视剧'}。`,
    keywords: `${meta.title},秋霞电影网,${type === 'movie' ? '电影在线观看,热门电影' : '电视剧全集,热门剧集'},${meta.desc}`,
  });

  const handleFilterChange = (next: FilterState) => {
    setFilters(next);
    setPage(1);
  };

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: meta.crumb }]} />

      <div className="mt-4 mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{meta.heading}</h1>
        <p className="mt-2 text-sm md:text-base text-foreground-600">{meta.desc}</p>
      </div>

      <FilterPanel filters={filters} onChange={handleFilterChange} showStatus={type === 'tv'} />

      <div className="flex items-center justify-between mt-5 mb-4">
        <p className="text-sm text-foreground-600">
          共找到 <span className="text-primary-700 font-semibold">{filtered.length}</span> 部{type === 'movie' ? '电影' : '电视剧'}
        </p>
        <span className="text-xs text-foreground-500 hidden sm:block">
          第 {safePage} / {totalPages} 页
        </span>
      </div>

      {paged.length > 0 ? (
        <>
          <div className="flex flex-col gap-3 md:gap-4">
            {paged.map((item) => (
              <TitleRow key={item.id} item={item} />
            ))}
          </div>
          <div className="mt-8">
            <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
          </div>
        </>
      ) : (
        <div className="py-16 flex flex-col items-center text-center">
          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 text-3xl mb-4">
            <i className="ri-emotion-sad-line"></i>
          </span>
          <p className="text-foreground-700 mb-4">
            暂无符合条件的{type === 'movie' ? '电影' : '电视剧'}，试试调整筛选条件
          </p>
          <button
            type="button"
            onClick={() => handleFilterChange({ ...defaultFilters })}
            className="h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            重置筛选条件
          </button>
        </div>
      )}
    </div>
  );
}